import React, { useState, useRef, useEffect } from "react";
import { Transition } from "@headlessui/react";
import ScoutStars from "@/assets/hd-icons/scout-stars.svg?react";
import ArrowRightIcon from "@icons/arrow-right.svg?react";
import RecordingIcon from "@icons/recording-03.svg?react";
import StopCircleIcon from "@icons/stop-circle.svg?react";
import XIcon from "@icons/x-close.svg?react";
import ChatMessage from "./ChatMessage";
import ScoutThinking from "./ScoutThinking";
import { ChatMessagePossiblyOptimistic } from "@/common/utils/types";
import { useAppSelector } from "@/common/hooks/useRedux";
import { getActiveDocument } from "@/features/patient-view/state/redux/activeDocumentsSlice";
import { processScoutRequest, buildScoutConversationHistory } from "@/common/utils/scoutRequestProcessor";
import { VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL } from "@/common/utils/constants";
import { AssemblyAI, StreamingTranscriber } from "assemblyai";
import { useScoutChatHistories } from "@/state/hooks/useScoutChat";
import { useScoutDropzone } from "@/state/hooks/useScoutDropzone";
import { useScoutDocumentHandling } from "@/state/hooks/useScoutDocumentHandling";
import * as Sentry from "@sentry/react";

interface ScoutChatProps {
    isOpen: boolean;
    onClose: () => void;
}

// Helper function to get effective session for impersonation
const getEffectiveSession = async () => {
    try {
        // Import ImpersonationService dynamically to avoid circular dependencies
        const impersonationModule = await import("@/common/lib/impersonationService");
        const ImpersonationService = impersonationModule.ImpersonationService;

        if (!ImpersonationService) {
            console.error("ImpersonationService not found in module");
            return null;
        }

        return await ImpersonationService.getEffectiveSession();
    } catch (error) {
        console.error("Error getting effective session:", error);
        return null;
    }
};

const ScoutChat: React.FC<ScoutChatProps> = ({ isOpen, onClose }) => {
    const [pendingUserInput, setPendingUserInput] = useState<string>("");
    const activeDocument = useAppSelector(getActiveDocument);
    const activeDocumentName = activeDocument?.chat_json?.header;

    // AssemblyAI streaming transcription state
    const [, setAccumulatedFinalText] = useState<string>("");
    const accumulatedFinalTextRef = useRef<string>("");
    const currentStreamingTextRef = useRef<string>("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const transcriberRef = useRef<any>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);

    const {
        query: { data: scoutChatHistories, isPending: isPendingScoutChatHistories },
        createChatHistoryMutation,
        addMessageMutation,
        clearChatHistoryMutation,
    } = useScoutChatHistories();
    const activeChatHistoryId = scoutChatHistories?.[0]?.id;
    const messages = scoutChatHistories?.[0]?.messages || [];

    const {
        handleAcceptDocumentAction,
        handleRejectDocumentAction,
        handleRetryDocumentAction,
        handlePreSendDocumentActions,
        handleSendReponseDocumentActions,
        handleDocumentError,
        cancelPendingDocumentActions,
    } = useScoutDocumentHandling();

    const {
        droppedPdfFile,
        cancelDroppedPdf,
        handleAcceptDropzoneAction,
        handleRejectDropzoneAction,
        getDropzoneRootProps,
        getDropzoneInputProps,
        isDragActive,
    } = useScoutDropzone({
        onDrop: cancelPendingDocumentActions,
    });

    // Create chat history if it doesn't exist
    useEffect(() => {
        if (isOpen && !activeChatHistoryId) {
            createChatHistoryMutation.mutate({});
        }
    }, [isOpen, activeChatHistoryId]);

    // Function to fetch temporary AssemblyAI token
    const fetchAssemblyAIToken = async (): Promise<string> => {
        try {
            const effectiveSession = await getEffectiveSession();
            if (!effectiveSession) {
                Sentry.captureException(new Error("No effective session found in fetchAssemblyAIToken"), {
                    tags: { errorCategory: "auth" },
                });
                throw new Error("No effective session found");
            }
            const response = await fetch(`${VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL}/assemblyai/token`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${effectiveSession.accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch AssemblyAI token: ${response.statusText}`);
            }

            const data = await response.json();
            return data.token || data.apiKey || data.key;
        } catch (error) {
            console.error("Error fetching AssemblyAI token:", error);
            throw error;
        }
    };

    // Clear chat history for current document
    const clearChatHistory = () => {
        if (activeChatHistoryId) {
            clearChatHistoryMutation.mutate({ chatHistoryId: activeChatHistoryId });
        }
    };

    // Add global error handler for AssemblyAI import issues
    useEffect(() => {
        const handleGlobalError = (event: ErrorEvent) => {
            if (event.message && event.message.includes("node")) {
                console.log("Caught AssemblyAI-related error, disabling recording");
                setIsAssemblyAIAvailable(false);
            }
        };

        window.addEventListener("error", handleGlobalError);
        return () => window.removeEventListener("error", handleGlobalError);
    }, []);

    // Cleanup function for streaming transcription
    useEffect(() => {
        return () => {
            // Cleanup on component unmount
            if (transcriberRef.current) {
                transcriberRef.current.close();
            }
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            }
            if (processorRef.current) {
                processorRef.current.disconnect();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    const [inputText, setInputText] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [recordingError, setRecordingError] = useState<string | null>(null);
    const [isAssemblyAIAvailable, setIsAssemblyAIAvailable] = useState<boolean | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = (scrollBehavior: "smooth" | "instant" = "smooth") => {
        // Give the component time to render before scrolling to bottom
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: scrollBehavior });
            textareaRef.current?.focus();
        }, 100);
    };

    const scrollTextareaToBottom = () => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
    };

    // Scroll to bottom when there are new messages or LLM is typing
    const messagesLength = messages.length;
    const prevMessagesLength = useRef(0);
    useEffect(() => {
        if (isOpen && (messagesLength !== prevMessagesLength.current || isThinking)) {
            const shouldSmoothScroll =
                (prevMessagesLength.current !== 0 && messagesLength > prevMessagesLength.current) || isThinking;
            scrollToBottom(shouldSmoothScroll ? "smooth" : "instant");
            prevMessagesLength.current = messagesLength;
        }
    }, [isOpen, messagesLength, isThinking]);

    // Auto-scroll textarea when input text changes
    useEffect(() => {
        scrollTextareaToBottom();
    }, [inputText]);

    const handleSendMessage = async (messageText?: string) => {
        const sendText = messageText || inputText;
        if (!sendText.trim()) return;

        // Snapshot history before this turn's optimistic message lands in the query cache.
        const conversationHistory = buildScoutConversationHistory(messages);

        try {
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: sendText,
                    isUser: true,
                    timestamp: new Date(),
                    actionStatus: null,
                },
            });

            if (!messageText) {
                setInputText("");
            }
            setPendingUserInput(sendText);
            setIsThinking(true);
            textareaRef.current?.focus();

            // Cancel any dropped files
            cancelDroppedPdf();

            // Pre-send document handling
            handlePreSendDocumentActions();

            // Call LLM API to process the input and update document
            const response = await processScoutRequest(sendText, activeDocument, conversationHistory);

            // Post-send document handling
            const isActionRequired = handleSendReponseDocumentActions(response);

            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: response.message,
                    isUser: false,
                    timestamp: new Date(),
                    actionIntent: response.success ? response.intent : undefined,
                    actionStatus: isActionRequired ? "pending" : null,
                },
            });
        } catch (error) {
            console.error("Error processing document update:", error);
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: "Sorry, I encountered an error processing your request. Please try again.",
                    isUser: false,
                    timestamp: new Date(),
                    actionStatus: null,
                },
            });
            handleDocumentError();
        } finally {
            setIsThinking(false);
        }
    };

    const handleAccept = async (message: ChatMessagePossiblyOptimistic) => {
        textareaRef?.current?.focus();
        await handleAcceptDocumentAction(message);
        await handleAcceptDropzoneAction(message.actionIntent);
    };

    const handleReject = async (message: ChatMessagePossiblyOptimistic) => {
        textareaRef?.current?.focus();
        await handleRejectDocumentAction(message);
        handleRejectDropzoneAction(message.actionIntent);
    };

    const handleRetry = async (messageId: string) => {
        setIsThinking(true);
        textareaRef?.current?.focus();
        await handleRetryDocumentAction(messageId, pendingUserInput);
        setIsThinking(false);
    };

    const handleAgain = async (messageId: string) => {
        const againMessage = messages.find((message) => message.id === messageId)?.text;
        if (againMessage) {
            setInputText(againMessage);
            textareaRef.current?.focus();
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey && !disableSend) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleStartRecording = async () => {
        try {
            setIsInitializing(true);
            setRecordingError(null);
            setAccumulatedFinalText(""); // Reset accumulated final text for new recording session
            accumulatedFinalTextRef.current = ""; // Reset ref for new recording session
            currentStreamingTextRef.current = ""; // Reset streaming text ref for new recording session

            // Fetch temporary API key from transcribe service
            const apiKey = await fetchAssemblyAIToken();

            if (!apiKey) {
                const errorMsg = "Failed to fetch AssemblyAI API key from transcribe service";
                console.error(errorMsg);
                setRecordingError(errorMsg);
                setIsInitializing(false);
                return;
            }

            // Get user media first
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                },
            });
            mediaStreamRef.current = stream;

            // Validate API key format
            if (!apiKey || typeof apiKey !== "string" || apiKey.trim().length === 0) {
                throw new Error("Invalid API key received from server");
            }

            // Initialize AssemblyAI client with temporary token
            try {
                new AssemblyAI({
                    apiKey: apiKey.trim(),
                });
            } catch (error) {
                console.error("Error creating AssemblyAI client:", error);
                throw new Error("Failed to initialize AssemblyAI client");
            }

            const effectiveSession = await getEffectiveSession();
            if (!effectiveSession) {
                Sentry.captureException(new Error("No effective session found in startListening"), {
                    tags: { errorCategory: "auth" },
                });
                throw new Error("No effective session found");
            }
            let keyword_prompts_text: string[] = [];
            try {
                const keyword_prompts = await fetch(
                    `${VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL}/prompts/veterinarian-words`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${effectiveSession.accessToken}`,
                        },
                    },
                );
                if (!keyword_prompts.ok) {
                    throw new Error("Failed to fetch keyword prompts");
                }
                const keyword_prompts_data = await keyword_prompts.json();
                keyword_prompts_text = keyword_prompts_data.text;
            } catch (error) {
                console.error("Error fetching keyword prompts:", error);
            }
            // Create streaming transcriber
            let transcriber;
            try {
                transcriber = new StreamingTranscriber({
                    token: apiKey.trim(),
                    sampleRate: 16_000,
                    formatTurns: true,
                    keytermsPrompt: keyword_prompts_text,
                });

                if (!transcriber) {
                    throw new Error("Failed to create transcriber");
                }
            } catch (error) {
                console.error("Error creating transcriber:", error);
                throw new Error("Failed to create streaming transcriber");
            }

            transcriberRef.current = transcriber;

            // Track connection state
            let isConnectionOpen = false;

            // Set up event listeners before connecting
            transcriber.on("open", (_data: any) => {
                isConnectionOpen = true;
                setIsInitializing(false);
                setIsRecording(true);
            });

            transcriber.on("close", (_data: any) => {
                setIsRecording(false);
            });

            transcriber.on("turn", (data: any) => {
                // If end_ofturn is true AND turn_is_formatted is true, add this transcript to accumulated final text
                if (data.end_of_turn === true && data.turn_is_formatted === true && data.transcript) {
                    const currentAccumulated = accumulatedFinalTextRef.current || "";
                    const newAccumulated = currentAccumulated
                        ? `${currentAccumulated} ${data.transcript}`
                        : data.transcript;
                    accumulatedFinalTextRef.current = newAccumulated;
                    setAccumulatedFinalText(newAccumulated);

                    // Clear streaming text since this is now final
                    currentStreamingTextRef.current = "";

                    // Set input to just the accumulated text (no streaming text)
                    setInputText(newAccumulated);
                } else if (data.transcript) {
                    // For streaming text, show accumulated final text + current streaming text
                    const currentStreaming = data.transcript;
                    currentStreamingTextRef.current = currentStreaming;
                    const currentAccumulated = accumulatedFinalTextRef.current || "";
                    const newInputText = currentAccumulated
                        ? `${currentAccumulated} ${currentStreaming}`
                        : currentStreaming;
                    setInputText(newInputText);
                }
            });

            transcriber.on("error", (error: any) => {
                console.error("Transcription error:", error);
                setRecordingError("Transcription error occurred. Please try again.");
                setIsRecording(false);
            });

            // Connect to AssemblyAI (this must be done after setting up listeners)
            try {
                await transcriber.connect();
                // Wait a moment for the connection to fully establish
                await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (connectError) {
                console.error("Error connecting to AssemblyAI:", connectError);
                throw new Error("Failed to connect to AssemblyAI transcription service");
            }

            // Create audio context
            const audioContext = new AudioContext({ sampleRate: 16_000 });
            audioContextRef.current = audioContext;

            // Create audio source
            const source = audioContext.createMediaStreamSource(stream);
            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            // Connect audio nodes
            source.connect(processor);
            processor.connect(audioContext.destination);

            // Process audio data - only send if transcriber is connected and has sendAudio method
            processor.onaudioprocess = (event) => {
                if (!isConnectionOpen || !transcriberRef.current) {
                    return;
                }

                try {
                    const inputData = event.inputBuffer.getChannelData(0);
                    if (!inputData || inputData.length === 0) {
                        return;
                    }

                    // AssemblyAI expects Int16Array for streaming
                    const audioData = new Int16Array(inputData.length);
                    for (let i = 0; i < inputData.length; i++) {
                        audioData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 0x7fff));
                    }

                    // Check if sendAudio method exists before calling
                    if (transcriberRef.current && typeof transcriberRef.current.sendAudio === "function") {
                        transcriberRef.current.sendAudio(audioData.buffer);
                    }
                } catch (error) {
                    console.error("Error sending audio:", error);
                    // Don't stop recording on individual audio send errors
                }
            };
        } catch (error) {
            console.error("Error starting recording:", error);
            setRecordingError("Failed to start recording. Please try again.");
            setIsRecording(false);
            setIsInitializing(false);

            // Clean up resources that may have been allocated before the error
            // Stop media stream tracks to release the microphone
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach((track) => track.stop());
                mediaStreamRef.current = null;
            }

            // Close transcriber if it was created
            if (transcriberRef.current) {
                try {
                    transcriberRef.current.close();
                } catch (closeError) {
                    console.error("Error closing transcriber during cleanup:", closeError);
                }
                transcriberRef.current = null;
            }

            // Disconnect audio processor if it was created
            if (processorRef.current) {
                try {
                    processorRef.current.disconnect();
                } catch (disconnectError) {
                    console.error("Error disconnecting processor during cleanup:", disconnectError);
                }
                processorRef.current = null;
            }

            // Close audio context if it was created
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (closeError) {
                    console.error("Error closing audio context during cleanup:", closeError);
                }
                audioContextRef.current = null;
            }
        }
    };

    const handleStopRecording = async () => {
        try {
            setIsRecording(false);
            setIsInitializing(false);
            setAccumulatedFinalText(""); // Reset accumulated final text when stopping recording
            accumulatedFinalTextRef.current = ""; // Reset ref when stopping recording
            currentStreamingTextRef.current = ""; // Reset streaming text ref when stopping recording

            // Close transcriber
            if (transcriberRef.current) {
                await transcriberRef.current.close();
                transcriberRef.current = null;
            }

            // Stop media stream
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach((track) => track.stop());
                mediaStreamRef.current = null;
            }

            // Disconnect audio processor
            if (processorRef.current) {
                processorRef.current.disconnect();
                processorRef.current = null;
            }

            // Close audio context
            if (audioContextRef.current) {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }
        } catch (error) {
            console.error("Error stopping recording:", error);
        }
    };

    const disableSend = isThinking || !activeChatHistoryId;

    return (
        <Transition show={isOpen} as={React.Fragment}>
            <div className="fixed inset-0 z-50 overflow-hidden pointer-events-none">
                {/* Modal positioned on the right */}
                <div
                    className="fixed right-4 bottom-[150px] w-96 max-w-md h-[750px] max-h-[calc(100vh-16rem)] min-h-[400px] z-50 rounded-xl overflow-hidden pointer-events-auto transition-shadow duration-200"
                    data-modal="document-chat"
                >
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-x-full"
                        enterTo="opacity-100 translate-x-0"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-x-0"
                        leaveTo="opacity-0 translate-x-full"
                        afterEnter={() => {
                            // Add shadow after slide-in completes
                            const modalElement = document.querySelector('[data-modal="document-chat"]') as HTMLElement;
                            if (modalElement) {
                                modalElement.style.boxShadow =
                                    "0 0 30px rgba(0, 0, 0, 0.4), 0 0 60px rgba(0, 0, 0, 0.2)";
                            }
                        }}
                        beforeLeave={() => {
                            // Remove shadow when leave animation starts so it fades with the modal
                            const modalElement = document.querySelector('[data-modal="document-chat"]') as HTMLElement;
                            if (modalElement) {
                                modalElement.style.boxShadow = "none";
                            }
                        }}
                    >
                        <div
                            {...getDropzoneRootProps({
                                className: "bg-white rounded-xl h-full flex flex-col relative",
                            })}
                        >
                            <input {...getDropzoneInputProps()} />
                            {/* Drag overlay */}
                            {isDragActive && (
                                <div className="absolute inset-0 bg-gray-100 bg-opacity-90 z-20 rounded-xl flex items-center justify-center border-2 border-max-800 border-dashed">
                                    <div className="text-gray-600 text-sm font-semibold px-16 text-center">
                                        Drop a PDF to create a custom document template
                                    </div>
                                </div>
                            )}
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <ScoutStars className="h-5 w-5" />
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 fraunces-soft">Scout</h3>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {messages && messages.length > 0 && (
                                        <button
                                            onClick={clearChatHistory}
                                            className="p-1 hover:bg-gray-100 rounded-md transition-colors text-xs text-gray-500 hover:text-gray-700"
                                            title="Clear chat history"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    <button
                                        onClick={onClose}
                                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                                    >
                                        <XIcon className="h-5 w-5 text-gray-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages Area */}
                            <div className="scout-messages flex-1 overflow-y-auto p-4 space-y-4 bg-white">
                                <ChatMessage
                                    key={1}
                                    message={{
                                        id: "1",
                                        text: "Hi! I'm Scout, your HappyDoc assistant. I can help you update and improve your documents, answer questions, and assist with your work. What would you like to work on?",
                                        isUser: false,
                                        timestamp: null,
                                        actionStatus: null,
                                    }}
                                />
                                {messages &&
                                    messages.map((message) => (
                                        <ChatMessage
                                            key={message.id}
                                            message={message}
                                            handleAccept={handleAccept}
                                            handleReject={handleReject}
                                            handleRetry={handleRetry}
                                            handleAgain={handleAgain}
                                            disableSend={disableSend}
                                        />
                                    ))}

                                {/* Loading state */}
                                {messages.length <= 1 && isPendingScoutChatHistories && (
                                    <div className="flex flex-col items-start gap-2">
                                        {Array.from({ length: 3 }).map((_, index) => (
                                            <div
                                                key={index}
                                                className={`animate-pulse w-${["2/3", "1/2", "3/4"][index % 3]}`}
                                            >
                                                <div className="h-3 bg-gray-200 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {isThinking && <ScoutThinking />}

                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Area */}
                            <div className="p-4 pt-1.5 border-t border-gray-200 bg-gray-100">
                                {/* Document Name and Dropped PDF Pills */}
                                {(activeDocumentName || droppedPdfFile) && (
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {droppedPdfFile ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-max-100 text-[10px] font-medium text-max-800 rounded-[4px]">
                                                {droppedPdfFile.name}
                                                <button
                                                    onClick={cancelDroppedPdf}
                                                    className="ml-0.5 hover:text-max-900"
                                                    title="Remove PDF"
                                                >
                                                    <XIcon className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ) : (
                                            <span className="inline-block px-2 py-1 bg-gray-200 text-[10px] text-gray-900 rounded-[4px]">
                                                {activeDocumentName}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Recording Error */}
                                {recordingError && (
                                    <div className="mb-2 p-2 bg-red-100 border border-red-300 rounded-md">
                                        <p className="text-sm text-red-700">{recordingError}</p>
                                        <button
                                            onClick={() => setRecordingError(null)}
                                            className="text-xs text-red-600 hover:text-red-800 underline mt-1"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                )}

                                {/* Initializing Message */}
                                {isInitializing && (
                                    <div className="mb-2 p-2 bg-blue-100 border border-blue-300 rounded-md">
                                        <p className="text-sm text-blue-700">
                                            Initializing voice recording... Please wait while we set up the
                                            transcription service.
                                        </p>
                                    </div>
                                )}

                                {/* AssemblyAI Not Available Message */}
                                {isAssemblyAIAvailable === false && (
                                    <div className="mb-2 p-2 bg-yellow-100 border border-yellow-300 rounded-md">
                                        <p className="text-sm text-yellow-700">
                                            Voice recording is not available in this browser. You can still type your
                                            message.
                                        </p>
                                    </div>
                                )}

                                <div className="relative">
                                    <textarea
                                        ref={textareaRef}
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        placeholder={
                                            activeDocumentName
                                                ? "Tell Scout how to edit " + activeDocumentName
                                                : "Ask Scout anything or tell me how to help"
                                        }
                                        className="block w-full px-3 py-2 pr-12 border-0 ring-1 ring-inset ring-gray-300 focused-inset rounded-md text-sm bg-white text-gray-900 placeholder-gray-500 resize-none"
                                        rows={3}
                                        disabled={isThinking}
                                    />
                                    {isInitializing ? (
                                        <div className="absolute right-[6px] bottom-[6px] flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Initializing...</span>
                                            <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        </div>
                                    ) : isRecording ? (
                                        <div className="absolute right-[6px] bottom-[6px] flex items-center gap-2">
                                            <span className="text-sm text-gray-600">Recording...</span>
                                            <button
                                                onClick={handleStopRecording}
                                                disabled={isThinking}
                                                className="w-6 h-6 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{ backgroundColor: "#FE2962" }}
                                                onMouseEnter={(e) =>
                                                    (e.currentTarget.style.backgroundColor = "#E01E5A")
                                                }
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.backgroundColor = "#FE2962")
                                                }
                                            >
                                                <StopCircleIcon className="h-4 w-4 text-white" />
                                            </button>
                                        </div>
                                    ) : !inputText.trim() ? (
                                        <button
                                            onClick={handleStartRecording}
                                            disabled={isThinking || isAssemblyAIAvailable === false}
                                            className="absolute right-[6px] bottom-[6px] w-6 h-6 bg-max-900 hover:bg-max-800 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={
                                                isAssemblyAIAvailable === false
                                                    ? "Voice recording not available"
                                                    : "Start voice recording"
                                            }
                                        >
                                            <RecordingIcon className="h-4 w-4 text-white" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleSendMessage()}
                                            disabled={!inputText.trim() || disableSend}
                                            className="absolute right-[6px] bottom-[6px] w-6 h-6 bg-max-900 hover:bg-max-800 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ArrowRightIcon className="h-4 w-4 text-white" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </div>
        </Transition>
    );
};

export default ScoutChat;
